import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table"
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Skeleton,
} from "@mui/material"
import { styled } from "@mui/material/styles"
import { useEffect, useMemo, useRef, useState } from "react"
import styles from "@/components/CRM/Search.module.css"
import { tableCellClasses } from "@mui/material/TableCell"
import AddIcon from "@mui/icons-material/Add"
import { toast } from "sonner"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiFetch } from "@/hooks/apiFetch"
import ConfirmDialog from "../Dialog/ConfirmDialog"
import { useCrudContext } from "@/context/crudContext"
import { useNavigate } from "react-router-dom"
import { useShortcut } from "@/hooks/useShortcut"
import DialogBoxComponent from "../Dialog/DialogBoxComponent"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { useCustomForm } from "@/hooks/useCustomForm"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { DevTool } from "@hookform/devtools"
import { getSubdomain } from "@/utilities/domain"

export function CustomTable({
  columns,
  apiPath,
  label,
  revalidateQuery,
  renderFilters,
  hardcodedFilters,
  onAdd,
  view = true,
  addBtn = true,
  action = true,
  showEdit = true,
  showDelete = true,
  onView,
  onEdit,
  onDelete,
  onExport,
  onImport,
  onEditImport = false,
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [data, setData] = useState([])
  const [meta, setMeta] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const { setOpenDialog, setSelectedRow: setModelSelectedRow } =
    useCrudContext()

  const addNewRef = useRef(null)
  useShortcut(["Control", "a"], () => addNewRef.current?.click())

  const queryString = new URLSearchParams({
    page: currentPage.toString(),
    search: searchQuery || "",
    ...Object.entries(hardcodedFilters || {}).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[`filter[${key}]`] = value
      }
      return acc
    }, {}),
  }).toString()

  const {
    data: apiResponse,
    isLoading,
    isFetched,
  } = useQuery({
    queryKey: [apiPath, currentPage, searchQuery, hardcodedFilters],
    queryFn: ({ signal }) => apiFetch(`${apiPath}?${queryString}`, { signal }),
  })

  useEffect(() => {
    if (apiResponse?.data) {
      // Add a check for apiResponse.data
      setData(apiResponse.data.data)
      setMeta(apiResponse.data.meta)
    }
  }, [apiResponse])

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }))

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }))

  const { mutate: deleteData } = useMutation({
    mutationFn: (data) =>
      apiFetch(`${apiPath}/${data.id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: revalidateQuery ? [revalidateQuery] : [apiPath],
      })
    },
  })

  const handleDelete = () => {
    if (selectedRow) {
      if (onDelete) {
        onDelete(selectedRow)
      } else {
        deleteData(selectedRow, {
          onSuccess: () => {
            toast.success(`Item deleted.`)
            setShowConfirmDialog(false)
            setSelectedRow(null)
          },
          onError: (error) => {
            toast.error(`Failed to delete the item: ${error.message}`)
          },
        })
      }
    }
  }

  const defaultOnAdd = () => {
    setModelSelectedRow(null)
    setOpenDialog(true)
  }

  const defaultOnView = (rowData) => {
    navigate(`/${apiPath}/${rowData.id}`)
  }

  const defaultOnEdit = (rowData) => {
    setModelSelectedRow(rowData)
    setOpenDialog(true)
  }

  // export start

  const ExportSchema = z.object({
    from: z
      .string()
      .optional()
      .refine((val) => !val || dayjs(val, "YYYY-MM-DD", true).isValid(), {
        message: "Invalid date format. Use YYYY-MM-DD.",
      })
      .transform((val) =>
        val ? dayjs(val).startOf("day").toDate() : undefined
      ),

    to: z
      .string()
      .optional()
      .refine((val) => !val || dayjs(val, "YYYY-MM-DD", true).isValid(), {
        message: "Invalid date format. Use YYYY-MM-DD.",
      })
      .transform((val) => (val ? dayjs(val).endOf("day").toDate() : undefined)),
  })

  const {
    api,
    handleSubmit: handleExportSubmit,
    isSubmitting: exportIsSubmitting,
    reset: exportReset,
    control: exportControl,
    Controller: ExportController,
  } = useCustomForm({})

  const [showExportDialog, setShowExportDialog] = useState(false)
  const submitExport = async (data) => {
    try {
      const token = localStorage.getItem("token") ?? ""
      const subdomain = getSubdomain()
      const baseUrl = `${import.meta.env.VITE_BASE_URL}`

      const response = await fetch(`${baseUrl}/export/${apiPath}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-Tenant": subdomain,
        },
        body: JSON.stringify({ ...data }),
      })

      if (!response.ok) {
        if (response.status === 401) {
          toast.error("Unauthorized. Please log in again.")
          return
        }
        // Try to parse error message from JSON body
        const err = await response.json().catch(() => ({}))
        toast.error(err.message || "Failed to export file")
        return
      }

      // Pull filename from Content-Disposition header if available
      const disposition = response.headers.get("Content-Disposition")
      const match = disposition?.match(/filename="?([^"]+)"?/)
      const fileName = match ? match[1] : "export.xlsx"

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      handleExportReset()
      setShowExportDialog(false)
      toast.success("Exported successfully")
    } catch (error) {
      console.error("Error downloading file:", error)
      toast.error("Failed to download file")
    }
  }
  const handleExportReset = () => {
    exportReset({
      from: null,
      to: null,
    })
  }

  const [increaseImport, setIncreaseImport] = useState(false)

  // import start
  const [showImportDialog, setShowImportDialog] = useState(false)

  const importSchema = z.object({
    file: z.instanceof(File, { message: "Please select a file" }),
  })

  // Function to fix URL by removing duplicate slashes and ensuring proper formatting
  const fixUrl = (url) => {
    if (!url) return url

    // Remove multiple consecutive slashes, except after
    return url.replace(/([^:]\/)\/+/g, "$1")
  }

  const {
    handleSubmit: handleImportSubmit,
    isSubmitting: importIsSubmitting,
    reset: importReset,
    control: importControl,
    Controller: ImportController,
  } = useCustomForm({
    resolver: zodResolver(importSchema),
  })

  const submitImport = async (data) => {
    try {
      // Convert File to Blob and create new FormData
      const blob = new Blob([data.file], { type: data.file.type })
      const formData = new FormData()
      formData.append("file", blob, data.file.name)
      if (increaseImport) formData.append("type", "increase")

      const method = "POST"
      const response = await api(`import/${apiPath}`, {
        method,
        body: formData,
        // Remove Content-Type header to let browser set correct multipart boundary
        headers: {},
      })

      if (response) {
        handleImportReset()
        setShowImportDialog(false)
        await queryClient.invalidateQueries({
          queryKey: revalidateQuery ? [revalidateQuery] : [apiPath],
        })
        toast.success(`Imported successfully`)
      }
    } catch (error) {
      console.error("Import failed:", error)
      toast.error("Failed to import file")
    }
  }
  const handleImportReset = () => {
    importReset({
      file: undefined,
    })
  }

  // import end

  const memoData = useMemo(
    () => (isLoading ? Array(5).fill({}) : data || []),
    [data, isLoading]
  )
  const memoColumns = useMemo(() => columns, [columns])

  const table = useReactTable({
    data: memoData,
    columns: memoColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  const onPageChange = (newPage) => {
    setCurrentPage(newPage)
  }

  return (
    <Card
      sx={{
        boxShadow: "none",
        borderRadius: "7px",
        mb: "25px",
        padding: { xs: "18px", sm: "20px", lg: "25px" },
      }}
      className="rmui-card"
    >
      <Box
        sx={{
          display: { xs: "block", sm: "flex" },
          alignItems: "center",
          justifyContent: "space-between",
          mb: "25px",
        }}
      >
        <Box
          component="form"
          className={styles.searchBox}
          sx={{
            width: { sm: "265px" },
          }}
        >
          <label>
            <i className="material-symbols-outlined">search</i>
          </label>
          <input
            type="text"
            className={styles.inputSearch}
            placeholder={`Search ${label.toLowerCase()} here.....`}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setCurrentPage(1) // Reset page to 1
            }}
          />
        </Box>
        <div className="space-x-2">
          {onEditImport && (
            <Button
              variant="outlined"
              onClick={() => {
                setIncreaseImport(true)
                setShowImportDialog(true)
              }}
              sx={{
                textTransform: "capitalize",
                borderRadius: "7px",
                fontWeight: "500",
                fontSize: "13px",
                padding: "6px 3px",
              }}
              color="primary"
            >
              Edit Import
            </Button>
          )}
          {onImport && (
            <Button
              variant="outlined"
              onClick={() => {
                setIncreaseImport(false)
                setShowImportDialog(true)
              }}
              sx={{
                textTransform: "capitalize",
                borderRadius: "7px",
                fontWeight: "500",
                fontSize: "13px",
                padding: "6px 3px",
              }}
              color="primary"
            >
              Import
            </Button>
          )}
          {renderFilters && renderFilters()}
          {onExport && (
            <Button
              variant="outlined"
              onClick={() => setShowExportDialog(true)}
              sx={{
                textTransform: "capitalize",
                borderRadius: "7px",
                fontWeight: "500",
                fontSize: "13px",
                padding: "6px 3px",
              }}
              color="primary"
            >
              Export
            </Button>
          )}
          {addBtn && (
            <Button
              onClick={onAdd ? onAdd : defaultOnAdd}
              ref={addNewRef}
              variant="outlined"
              sx={{
                textTransform: "capitalize",
                borderRadius: "7px",
                fontWeight: "500",
                fontSize: "13px",
                padding: "6px 13px",
              }}
              color="primary"
            >
              <AddIcon sx={{ position: "relative", top: "-1px" }} />{" "}
              {`Add New ${label}`}
            </Button>
          )}
        </div>
      </Box>
      <Box
        sx={{
          marginLeft: "-25px",
          marginRight: "-25px",
        }}
      >
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            borderRadius: "0",
          }}
          className="rmui-table"
        >
          <Table aria-label="Table">
            <TableHead className="bg-primary-50">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  <TableCell
                    sx={{
                      fontWeight: "500",
                      padding: "10px 20px",
                      fontSize: "14px",
                    }}
                    className="text-black border-bottom"
                  >
                    {"SN"}
                  </TableCell>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sx={{
                        fontWeight: "500",
                        padding: "10px 20px",
                        fontSize: "14px",
                      }}
                      className="text-black border-bottom"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableCell>
                  ))}
                  {action && (
                    <TableCell
                      sx={{
                        fontWeight: "500",
                        padding: "10px 20px",
                        fontSize: "14px",
                      }}
                      className="text-black border-bottom"
                    >
                      Action
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell
                      sx={{
                        color: "GrayText",
                        fontWeight: "500",
                        padding: "6px 14px",
                        fontSize: "14px",
                      }}
                    >
                      {isLoading ? <Skeleton /> : "#" + (row.index + 1)}
                    </StyledTableCell>
                    {row.getVisibleCells().map((cell) => (
                      <StyledTableCell
                        key={cell.id}
                        sx={{
                          fontWeight: "500",
                          padding: "6px 14px",
                          fontSize: "14px",
                        }}
                        className="text-black border-bottom"
                      >
                        {isLoading ? (
                          <Skeleton />
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </StyledTableCell>
                    ))}

                    {action && (
                      <StyledTableCell sx={{ padding: "15px 20px" }}>
                        {isLoading ? (
                          <Skeleton />
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {view && (
                              <IconButton
                                aria-label="view"
                                color="primary"
                                sx={{ padding: "5px" }}
                                onClick={() => {
                                  onView
                                    ? onView(row.original)
                                    : defaultOnView(row.original)
                                }}
                              >
                                <i
                                  className="material-symbols-outlined"
                                  style={{
                                    fontSize: "16px",
                                  }}
                                >
                                  visibility
                                </i>
                              </IconButton>
                            )}

                            {showEdit && (
                              <IconButton
                                aria-label="edit"
                                color="secondary"
                                sx={{ padding: "5px" }}
                                onClick={() => {
                                  onEdit
                                    ? onEdit(row.original)
                                    : defaultOnEdit(row.original)
                                }}
                              >
                                <i
                                  className="material-symbols-outlined"
                                  style={{
                                    fontSize: "16px",
                                  }}
                                >
                                  edit
                                </i>
                              </IconButton>
                            )}

                            {showDelete && (
                              <IconButton
                                aria-label="delete"
                                color="error"
                                sx={{ padding: "5px" }}
                                onClick={() => {
                                  setSelectedRow(row.original)
                                  setShowConfirmDialog(true)
                                }}
                              >
                                <i
                                  className="material-symbols-outlined"
                                  style={{
                                    fontSize: "16px",
                                  }}
                                >
                                  delete
                                </i>
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length + 2}>
                    No results found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {meta?.total > 0 && meta?.links?.length > 0 && (
            <div className="flex justify-between items-center mt-4 px-4">
              <div className="text-sm text-gray-600">
                Showing <span className="font-medium">{meta.from}</span> to
                <span className="font-medium">{meta.to}</span> of
                <span className="font-medium">{meta.total}</span> results
              </div>

              <div className="inline-flex space-x-1">
                {/* Previous button */}
                <button
                  onClick={() => {
                    if (meta.current_page > 1) {
                      onPageChange(meta.current_page - 1)
                    }
                  }}
                  disabled={meta.current_page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  Previous
                </button>

                {/* Page numbers */}
                {meta.links.slice(1, -1).map((link, i) => {
                  // Skip the "..." links
                  if (link.label === "...") return null

                  const pageNum = parseInt(link.label)
                  return (
                    <button
                      key={i}
                      onClick={() => onPageChange(pageNum)}
                      className={`px-3 py-1 border rounded-md ${
                        meta.current_page === pageNum
                          ? "bg-primary-500 text-white border-primary-500"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      {link.label}
                    </button>
                  )
                })}

                {/* Next button */}
                <button
                  onClick={() => {
                    if (meta.current_page < meta.last_page) {
                      onPageChange(meta.current_page + 1)
                    }
                  }}
                  disabled={meta.current_page === meta.last_page}
                  className="px-3 py-1 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </TableContainer>
      </Box>

      <ConfirmDialog
        open={showConfirmDialog}
        title="Are you sure?"
        description={`Are you sure you want to delete this item?`}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleDelete}
        closeText="Close"
        confirmText="Confirm"
      />

      <DialogBoxComponent
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        header={<span>Export {label}</span>}
        body={
          <>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <form
                onSubmit={handleExportSubmit(submitExport)}
                style={{ maxWidth: "400px", margin: "auto" }}
              >
                <Box mt={2} mb={2}>
                  <ExportController
                    name="from"
                    control={exportControl}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        label="From Date"
                        value={value ? dayjs(value) : null}
                        onChange={(newValue) => {
                          onChange(
                            newValue
                              ? dayjs(newValue).format("YYYY-MM-DD")
                              : null
                          )
                        }}
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message,
                            fullWidth: true,
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <Box mb={2}>
                  <ExportController
                    name="to"
                    control={exportControl}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        label="To Date"
                        value={value ? dayjs(value) : null}
                        onChange={(newValue) => {
                          onChange(
                            newValue
                              ? dayjs(newValue).format("YYYY-MM-DD")
                              : null
                          )
                        }}
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message,
                            fullWidth: true,
                          },
                        }}
                      />
                    )}
                  />
                </Box>
                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outlined"
                    disabled={exportIsSubmitting}
                    onClick={() => {
                      setShowExportDialog(false)
                      handleExportReset()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={exportIsSubmitting}
                  >
                    Submit
                  </Button>
                </div>
              </form>
              <DevTool control={exportControl} />
            </LocalizationProvider>
          </>
        }
      />

      <DialogBoxComponent
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        header={<span>Import {label}</span>}
        body={
          <>
            <form
              onSubmit={handleImportSubmit(submitImport)}
              style={{ maxWidth: "400px", margin: "auto" }}
            >
              <Box mt={2} mb={2}>
                <ImportController
                  name="file"
                  control={importControl}
                  render={({ field: { onChange }, fieldState: { error } }) => (
                    <>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error("File size should be less than 5MB")
                              return
                            }
                            onChange(file)
                          }
                        }}
                        className="form-control"
                        placeholder="Choose file"
                      />
                      {error && (
                        <div
                          style={{
                            color: "red",
                            fontSize: "0.8rem",
                            marginTop: "4px",
                          }}
                        >
                          {error.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </Box>

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outlined"
                  disabled={exportIsSubmitting}
                  onClick={() => {
                    setShowImportDialog(false)
                    handleImportReset()
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={importIsSubmitting}
                >
                  Submit
                </Button>
              </div>
            </form>
          </>
        }
      />
    </Card>
  )
}
